import { Dayjs } from "dayjs";

export enum SchedulingStrategyType {
    AfterEmployeeCreationSchedulingStrategy = "AfterEmployeeCreationSchedulingStrategy",
    DefinedBySubtypeSchedulingStrategy = "DefinedBySubtypeSchedulingStrategy",
    RepeatPatternSchedulingStrategy = "RepeatPatternSchedulingStrategy",
    ScheduleDefinitionSchedulingStrategy = "ScheduleDefinitionSchedulingStrategy",
}

export interface BaseSchedulingStrategy {
    type: SchedulingStrategyType;
}

export function isBaseSchedulingStrategy(value: unknown): value is BaseSchedulingStrategy {
    return (
        typeof value === "object" &&
        value !== null &&
        "type" in value &&
        Object.values(SchedulingStrategyType).includes((value as BaseSchedulingStrategy).type)
    );
}

export enum ActionModuleFrequency {
    Daily = "Daily",
    Weekly = "Weekly",
    Monthly = "Monthly",
    Yearly = "Yearly",
}

export interface RepeatPatternSchedulingStrategyModel extends BaseSchedulingStrategy {
    frequency: ActionModuleFrequency
}

export function isRepeatPatternSchedulingStrategyModel(value: unknown): value is RepeatPatternSchedulingStrategyModel {
    return (
        isBaseSchedulingStrategy(value) &&
        value.type === SchedulingStrategyType.RepeatPatternSchedulingStrategy &&
        "frequency" in value &&
        Object.values(ActionModuleFrequency).includes((value as RepeatPatternSchedulingStrategyModel).frequency)
    );
}

export interface ScheduleDefinitionSchedulingStrategyModel extends BaseSchedulingStrategy {
   scheduleDefinition: ScheduleDefinitionBase;
}

export function isScheduleDefinitionSchedulingStrategyModel(value: unknown): value is ScheduleDefinitionSchedulingStrategyModel {
    return (
        isBaseSchedulingStrategy(value) &&
        value.type === SchedulingStrategyType.ScheduleDefinitionSchedulingStrategy &&
        "scheduleDefinition" in value
    );
}

export interface ScheduleDefinitionBase {
    id?: number;
    discriminator: ScheduleDefinitionType;
    startDate: string | Date | null;
    endDate: string | Date | null;
    combineConsecutiveDays: boolean;
    separateByWeek: boolean;
    separateByMonth: boolean;
}

export enum ScheduleDefinitionType {
    Time = "Time",
    Day = "Day",
    Week = "Week",
    Month = "Month",
    Quarter = "Quarter"
}

export interface ScheduleDaysDefinition extends ScheduleDefinitionBase {
    days: ScheduleDay[];
}

export interface ScheduleWeekDefinition extends ScheduleDefinitionBase {
    week: ScheduleWeek;
}

export interface ScheduleMonthDefinition extends ScheduleDefinitionBase {
    month: ScheduleMonth;
}

export interface ScheduleQuarterDefinition extends ScheduleDefinitionBase {
    quarter: ScheduleQuarter;
}

export interface ScheduleTimePeriodsDefinition extends ScheduleDefinitionBase {
    timePeriods: ScheduleTimePeriod[];
}

export interface ScheduleDay {
    dayOfWeek: DayOfWeek;
    startTime: Dayjs | null;
    endTime: Dayjs | null;
}

export enum DayOfWeek {
    Monday = "Monday",
    Tuesday = "Tuesday",
    Wednesday = "Wednesday",
    Thursday = "Thursday",
    Friday = "Friday",
    Saturday = "Saturday",
    Sunday = "Sunday"
}

export interface ScheduleWeek {
    mode: ScheduleWeekMode;
    value: number;
    days?: ScheduleDay[];
}

export enum ScheduleWeekMode {
    None = "None",
    Repeat = "Repeat",
    Skip = "Skip"
}

export interface ScheduleMonth {
    mode: ScheduleMonthMode;
    value: number;
    weekOffsetInsideMonth: number;
    week?: ScheduleWeek;
}

export enum ScheduleMonthMode {
    None = "None",
    Repeat = "Repeat",
    Skip = "Skip"
}

export interface ScheduleQuarter {
    mode: ScheduleQuarterMode;
    value: number;
    monthOffsetInsideQuarter: number;
    month?: ScheduleMonth;
}

export enum ScheduleQuarterMode {
    None = "None",
    Repeat = "Repeat",
    Skip = "Skip"
}

export interface ScheduleTimePeriod {
    startTime: Date | null;
    endTime: Date | null;
}
